import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { WaveInstance } from '@ov-suite/models-warehouse';
import { MatCustomDataSource } from '@ov-suite/ui';
import { ColumnData } from '@ov-suite/helpers-shared';
import { OvAutoService } from '@ov-suite/services';
import gql from 'graphql-tag';
import { getKeys } from '@ov-suite/graphql-helpers';
import { getFieldMetadata } from '@ov-suite/ov-metadata';
import moment from 'moment';
import { PickDetailModel } from '../pick-detail.model';

@Component({
  selector: 'ov-suite-pick-sheet',
  templateUrl: './pick-list.component.html',
  styleUrls: ['./pick-list.component.css'],
})
export class PickSheetComponent implements OnInit, OnChanges {
  @Input() loadId: number | null = null;

  dataSource: MatCustomDataSource<PickDetailModel> = new MatCustomDataSource<PickDetailModel>([]);

  private fetchSequence = 0;

  columnData: ColumnData<PickDetailModel>[] = [
    {
      title: 'Pick Type',
      type: 'other',
      action: item => {
        if (!item.load) {
          return 'Stage Supply';
        }
        if (!item.pickingItem.productSku) {
          return 'Stage Pick';
        }
        return 'Regular Pick';
      },
      keys: [],
      disableSorting: true,
    },
    {
      title: 'Product / Box',
      type: 'other',
      action: ({ pickingItem }) =>
        pickingItem?.productSku?.name ?? pickingItem.containerPicked?.name ?? pickingItem.inventoryPicked?.productSku?.name ?? 'Unknown',
      keys: ['pickingItem.productSku.name'],
      disableSorting: true,
    },
    {
      type: 'number',
      title: 'Quantity',
      key: 'quantity',
      disableSorting: true,
    },
    {
      type: 'other',
      title: 'Scheduled Time',
      action: item => {
        if (item.load?.wave) {
          return this.getTime(item.load.wave, 'HH:mm DD-MM-YYYY');
        }
        if (item.pickingItem.createdDate) {
          return moment(item.pickingItem.createdDate).format('HH:mm DD-MM-YYYY');
        }
        return '';
      },
      keys: [],
    },
  ];

  constructor(private readonly ovAutoService: OvAutoService) {}

  ngOnInit(): void {
    this.getData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.loadId && !changes.loadId.firstChange) {
      this.getData();
    }
  }

  private buildQuery(loadId: number | null) {
    const selection = this.getPickDetailSelection();

    if (loadId != null) {
      return gql(`query listPickDetailsForLoad($loadId: Int!) {
        listPickDetailsForLoad(loadId: $loadId) {
          ${selection}
        }
      }`);
    }

    return gql(`query listAllPickDetails {
      listAllPickDetails {
        ${selection}
      }
    }`);
  }

  private getPickDetailSelection(): string {
    return getKeys({
      name: '',
      input: PickDetailModel,
      metadata: getFieldMetadata(PickDetailModel),
      api: '',
      keys: [
        'quantity',
        'pickingItem',
        'pickingItem.productSku',
        'pickingItem.productSku.name',
        'load',
        'load.id',
        'pickingItem.inventoryPicked',
        'pickingItem.containerPicked',
        'pickingItem.containerPicked.id',
        'pickingItem.containerPicked.name',
        'pickingItem.createdDate',
        'pickingItem.loadId',
        'pickingItem.pickStatus',
        'load.wave',
        'load.wave.startDate',
        'load.wave.waveConfig',
        'load.wave.waveConfig.startTime',
      ],
    });
  }

  private getData() {
    const requestId = ++this.fetchSequence;
    const loadId = this.loadId;

    this.ovAutoService.apollo
      .query<{ listAllPickDetails?: PickDetailModel[]; listPickDetailsForLoad?: PickDetailModel[] }>({
        fetchPolicy: 'no-cache',
        query: this.buildQuery(loadId),
        variables: loadId != null ? { loadId } : undefined,
      })
      .subscribe(response => {
        if (requestId !== this.fetchSequence) {
          return;
        }

        this.dataSource.data = response.data.listPickDetailsForLoad ?? response.data.listAllPickDetails ?? [];
      });
  }

  getTime(wave: WaveInstance, format: string): string {
    const day = wave.startDate;
    const time = wave.waveConfig.startTime;

    return moment(`${day} ${time}`).format(format);
  }
}
